import { observable, computed, reaction, asMap, action, runInAction } from 'mobx'

import { get } from 'transport/sitewide'
import { getSearchResults as getFaculty } from 'transport/faculty'

import UiStore from 'stores/UiStore'

import ProjectStore from 'stores/ProjectStore'
import ResourceStore from 'stores/ResourceStore'
import ExpertiseStore from 'stores/ExpertiseStore'

import FacultyModel from 'models/FacultyModel'
import ProjectModel from 'models/ProjectModel'
import ResourceModel from 'models/ResourceModel'

import { debounce, modelFromJS } from 'utils'

class SearchStore {
  @observable triggerCount = 0

  @observable pages = asMap({})

  @observable facultySearchResults = []

  @computed get generics () {
    const pageIds = this.pages.get(UiStore.pagination.current)

    if (!pageIds) {
      return []
    }

    return pageIds.map(({ id, type }) => (
      type === 'Project'
        ? ProjectStore.projectsMap.get(id)
        : ResourceStore.resourcesMap.get(id)
    )).filter(a => a)
  }

  constructor () {
    // mobx reaction disposers
    this.fetcher = null
    this.filterReset = null
  }

  @action async fetchGenerics () {
    if (UiStore.sitewideSearchFilters.search === '') {
      return
    }

    const response = await get(UiStore.pagination.current, UiStore.sitewideSearchFilters)
    const toBeMergedList = response.results
      .map(modelFromJS)

    runInAction(() => {
      // update pagination
      UiStore.pagination.current = response.current_page
      UiStore.pagination.last = response.last_page

      // merge new projects into this.projectsMap
      for (let generic of toBeMergedList) {
        if (generic instanceof ProjectModel) {
          ProjectStore.projectsMap.set(generic.id, generic)
        } else if (generic instanceof ResourceModel) {
          ResourceStore.resourcesMap.set(generic.id, generic)
        }
      }

      // remember the ordered project ids for this page
      this.pages.set(UiStore.pagination.current, toBeMergedList.map(generic => {
        if (generic instanceof ProjectModel) {
          return { id: generic.id, type: 'Project' }
        } else if (generic instanceof ResourceModel) {
          return { id: generic.id, type: 'Resource' }
        }
      }))
    })
  }

  @action async fetchFacultySearchResults () {
    if (UiStore.sitewideSearchFilters.search === '') {
      this.facultySearchResults = []
      return
    }

    const response = await getFaculty(UiStore.sitewideSearchFilters.search)
    runInAction(() => {
      this.facultySearchResults = response.map(json => new FacultyModel(json))
    })
  }

  // proxy to ExpertiseStore action
  @action fetchKeywordHints () {
    UiStore.expertiseFilters.search = UiStore.sitewideSearchFilters.search
    ExpertiseStore.fetchKeywordHints()
  }

  startFetcher () {
    this.fetchGenerics()
    this.fetchFacultySearchResults()
    this.fetchKeywordHints()

    this.filterReset = reaction(
      () => [
        UiStore.sitewideSearchFilters.search
      ],
      debounce(::this.restart, 500)
    )

    this.fetcher = reaction(
      // dependencies to watch
      () => [
        this.triggerCount,
        UiStore.pagination.current
      ],
      // action
      () => {
        if (!this.pages.get(UiStore.pagination.current)) {
          this.fetchGenerics()

          if (UiStore.pagination.current === 1) {
            this.fetchFacultySearchResults()
            this.fetchKeywordHints()
          }
        }
      }
    )
  }

  @action restart () {
    this.clearAll()
    UiStore.resetPagination()
    this.triggerCount += 1
    UiStore.updateSitewideSearchFiltersInUrl()
  }

  @action stopFetcher () {
    if (this.fetcher) {
      // dispose reactions
      this.fetcher()
      this.filterReset()

      // clear from memory
      this.clearAll()

      // reset pagination
      UiStore.resetPagination()

      // reset filters
      UiStore.resetSitewideSearchFilters()
    }
  }

  @action clearAll () {
    ProjectStore.projectsMap.clear()
    ResourceStore.resourcesMap.clear()
    ExpertiseStore.keywordHints = []
    this.facultySearchResults = []
    this.clearPages()
  }

  @action clearPages () {
    this.pages.clear()
  }
}

const singleton = new SearchStore()
export default singleton



// WEBPACK FOOTER //
// ./resources/assets/js/stores/SearchStore.js